import { Id, isSuccess, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { archivedEventService } from "../shared/api/event/archivedEventService";
import { eventService } from "../shared/api/event/eventService";

export const useEventActions = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { showToast } = useToasts();

	const restoreEvent = useCallback(
		async (eventId: Id | null) => {
			if (!eventId) {
				return void 0;
			}

			const response = await archivedEventService.restore(eventId);

			if (isSuccess(response)) {
				void queryClient.invalidateQueries({ queryKey: eventService.calendarKey });
				void queryClient.invalidateQueries({ queryKey: eventService.listOverviewKey });
				void queryClient.invalidateQueries({ queryKey: eventService.listKey });
				void queryClient.invalidateQueries({ queryKey: archivedEventService.listKey });
				navigate(`/events/active/${eventId}/details`);
				showToast({
					type: "success",
					title: t("Event"),
					body: t("Event was restored."),
				});
			}
		},
		[queryClient, navigate, showToast, t],
	);

	const deleteEvent = useCallback(
		async (eventId: Id) => {
			const response = await archivedEventService.destroy(eventId);

			if (isSuccess(response)) {
				showToast({
					type: "success",
					title: t("Event"),
					body: t("Event was deleted."),
				});
				void queryClient.invalidateQueries({ queryKey: archivedEventService.listKey });
				void queryClient.invalidateQueries({ queryKey: eventService.filteredArchivedIndexKey });
			}
		},
		[queryClient, showToast, t],
	);

	return {
		restoreEvent,
		deleteEvent,
	};
};
